import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SlidersHorizontal, Loader2, RotateCcw } from "lucide-react";

const FIELDS = [
  ["margin_pct", "Margin", "%", "Applied to every new bid unless the estimator sets one. 0 means nothing is added silently."],
  ["contingency_pct", "Contingency", "%", "Risk allowance on direct cost."],
  ["bond_pct", "Bond", "%", "Only when the client requires a bond."],
  ["warranty_pct", "Warranty reserve", "%", "Set aside for warranty work."],
  ["validity_days", "Proposal validity", "days", "How long a price stands on the proposal."],
  ["warranty_term_years", "Warranty term", "years", "Printed on the proposal."],
];

// Settings → Company → Defaults: the one table bids read; the connector reports what it applied from here.
export function BidDefaults() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/company/defaults").then((r) => { setD(r.data); setForm(r.data.defaults); }).catch(() => setD({ defaults: {}, can_edit: false }));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const dirty = Object.keys(form).some((k) => String(form[k]) !== String(d.defaults[k]));
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/company/defaults", form);
      toast.success(r.data.changed?.length ? `Defaults saved: ${r.data.changed.join(", ")}` : "No changes");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save defaults"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="p-5 mt-6 space-y-4" data-testid="bid-defaults-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-400"><SlidersHorizontal className="w-3.5 h-3.5" /> Bid defaults</div>
          <p className="text-xs text-slate-500 mt-1">Every new bid starts from this table. Nothing else in the app defaults a number; the bid and the connector list what came from here.</p>
        </div>
        {d.updated_at && <span className="text-[11px] text-slate-400" data-testid="bid-defaults-meta">Last changed by {d.updated_by} · {new Date(d.updated_at).toLocaleDateString()}</span>}
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {FIELDS.map(([k, label, unit, help]) => (
          <div key={k}>
            <Label className="text-xs text-slate-500">{label} <span className="text-slate-300">({unit})</span></Label>
            <Input type="number" step={unit === "%" ? "0.1" : "1"} min={d.ranges?.[k]?.[0]} max={d.ranges?.[k]?.[1]} value={form[k] ?? ""} disabled={!d.can_edit}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 font-mono" data-testid={`bid-default-${k}`} />
            <p className="text-[11px] text-slate-400 mt-1">{help}</p>
          </div>
        ))}
      </div>
      {d.can_edit && (
        <div className="flex items-center gap-2">
          <Button onClick={save} disabled={saving || !dirty} className="bg-[#003E87] hover:bg-[#00305F]" data-testid="bid-defaults-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Save defaults
          </Button>
          <Button variant="outline" onClick={() => setForm(d.factory)} data-testid="bid-defaults-factory" title="Load the factory values into the form (saves only when you press Save)">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Factory values
          </Button>
        </div>
      )}
    </Card>
  );
}
